import React, { useMemo } from 'react';
import { clsx } from 'clsx';
import styles from './GalleryDots.module.css';

const DOT_SIZE = 4; // px
const DOT_GAP = 4; // px

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const GalleryDots = ({
  count = 0,
  activeIndex = 0,
  className,
  maxVisible = 7,
  onDotClick
}) => {
  const normalizedCount = Math.max(count, 0);
  const safeActiveIndex = clamp(
    activeIndex,
    0,
    normalizedCount > 0 ? normalizedCount - 1 : 0
  );
  const visibleCount = Math.min(normalizedCount, maxVisible);

  const startIndex = useMemo(() => {
    if (normalizedCount === 0) return 0;
    if (normalizedCount <= visibleCount) return 0;
    const half = Math.floor(visibleCount / 2);
    let start = safeActiveIndex - half;

    // Balance the window for even maxVisible counts
    if (visibleCount % 2 === 0) {
      start += 1;
    }

    start = Math.max(0, start);
    start = Math.min(start, normalizedCount - visibleCount);
    return start;
  }, [normalizedCount, visibleCount, safeActiveIndex]);

  if (normalizedCount === 0) return null;

  const offset = startIndex * (DOT_SIZE + DOT_GAP);
  const isInteractive = typeof onDotClick === 'function';

  return (
    <div
      className={clsx(styles.wrapper, className)}
      style={{ '--visible-count': visibleCount }}
    >
      <div
        className={styles.track}
        style={{ transform: `translateX(-${offset}px)` }}
      >
        {Array.from({ length: normalizedCount }).map((_, index) => {
          const handleClick = (event) => {
            if (!isInteractive) return;
            event.stopPropagation();
            onDotClick(index);
          };

          const handleKeyDown = (event) => {
            if (!isInteractive) return;
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.stopPropagation();
              onDotClick(index);
            }
          };

          return (
            <div
              key={index}
              className={clsx(styles.dotButton, { [styles.interactive]: isInteractive })}
              role={isInteractive ? 'button' : undefined}
              tabIndex={isInteractive ? 0 : -1}
              aria-disabled={!isInteractive}
              aria-label={`Go to image ${index + 1}`}
              aria-pressed={index === safeActiveIndex}
              onClick={handleClick}
              onKeyDown={handleKeyDown}
            >
              <span
                className={clsx(styles.dot, {
                  [styles.active]: index === safeActiveIndex,
                })}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GalleryDots;
